import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { GroupWrapper, StyledCheckboxGroup, StyledCheckboxWrapper } from './styles/checkbox.style';

// --- Types ---

export interface CheckboxChangeEventTarget extends EventTarget {
  checked: boolean;
  value: any;
  name: string;
  type: string;
}

export interface CheckboxChangeEvent extends Omit<React.ChangeEvent<HTMLInputElement>, 'target'> {
  target: CheckboxChangeEventTarget;
}

// Extra wrapper props (e.g. the hover/focus handlers a Tooltip injects) land on the <label>.
export interface CheckboxProps
  extends Omit<
    React.LabelHTMLAttributes<HTMLLabelElement>,
    'onChange' | 'onClick' | 'defaultChecked' | 'children'
  > {
  checked?: boolean;
  defaultChecked?: boolean;
  disabled?: boolean;
  indeterminate?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClick?: React.MouseEventHandler<HTMLElement>;
  name?: string;
  value?: any;
  id?: string;
  autoFocus?: boolean;
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  tabIndex?: number;
}

export interface CheckboxOptionType {
  label: React.ReactNode;
  value: any;
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export interface CheckboxGroupProps {
  options?: (CheckboxOptionType | string | number)[];
  value?: any[];
  defaultValue?: any[];
  onChange?: (checkedValue: any[]) => void;
  disabled?: boolean;
  name?: string;
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

// --- Context ---

interface CheckboxGroupContextValue {
  toggleOption: (option: { label: React.ReactNode; value: any }) => void;
  value: any[];
  disabled?: boolean;
  name?: string;
  registerValue: (val: any) => void;
  cancelValue: (val: any) => void;
}

const GroupContext = createContext<CheckboxGroupContextValue | null>(null);

// --- Helpers ---

const ANTISH = 'antish';

function cx(...classes: (string | undefined | false | null)[]): string {
  return classes.filter(Boolean).join(' ');
}

// --- Checkbox Component ---

export interface CheckboxRef {
  input: HTMLInputElement | null;
  nativeElement: HTMLLabelElement | null;
}

const InternalCheckbox = React.forwardRef<CheckboxRef, CheckboxProps>(
  (
    {
      checked: checkedProp,
      defaultChecked = false,
      disabled = false,
      indeterminate = false,
      onChange,
      onClick,
      name,
      value,
      id,
      autoFocus,
      children,
      className,
      style,
      tabIndex,
      ...rest
    },
    ref
  ) => {
    const group = useContext(GroupContext);
    const inputRef = useRef<HTMLInputElement>(null);
    const labelRef = useRef<HTMLLabelElement>(null);

    // Expose ref
    React.useImperativeHandle(ref, () => ({
      input: inputRef.current,
      nativeElement: labelRef.current,
    }));

    // Resolve checked state
    const [internalChecked, setInternalChecked] = useState(defaultChecked);

    const isControlled = checkedProp !== undefined;
    const isInGroup = group !== null;

    let mergedChecked: boolean;
    if (isInGroup) {
      mergedChecked = group.value.includes(value);
    } else if (isControlled) {
      mergedChecked = checkedProp;
    } else {
      mergedChecked = internalChecked;
    }

    const mergedDisabled = isInGroup ? (group.disabled ?? disabled) : disabled;
    const mergedName = isInGroup ? group.name : name;

    // Register/unregister with group
    useEffect(() => {
      if (isInGroup) {
        group.registerValue(value);
        return () => group.cancelValue(value);
      }
    }, [isInGroup, value]); // eslint-disable-line react-hooks/exhaustive-deps

    // Set indeterminate on native input (DOM property, not attribute)
    useEffect(() => {
      if (inputRef.current) {
        inputRef.current.indeterminate = indeterminate;
      }
    }, [indeterminate]);

    // AutoFocus
    useEffect(() => {
      if (autoFocus && inputRef.current) {
        inputRef.current.focus();
      }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        if (mergedDisabled) return;

        const newChecked = e.target.checked;

        if (!isControlled && !isInGroup) {
          setInternalChecked(newChecked);
        }

        onChange?.(e);

        if (isInGroup) {
          group.toggleOption({ label: children, value });
        }
      },
      [mergedDisabled, isControlled, isInGroup, value, onChange, children, group]
    );

    const wrapperClassName = cx(
      ANTISH,
      'ant-checkbox-wrapper',
      mergedChecked && 'ant-checkbox-wrapper-checked',
      mergedDisabled && 'ant-checkbox-wrapper-disabled',
      indeterminate && !mergedChecked && 'ant-checkbox-wrapper-indeterminate',
      className
    );

    const boxClassName = cx(
      'ant-checkbox',
      mergedChecked && 'ant-checkbox-checked',
      mergedDisabled && 'ant-checkbox-disabled',
      indeterminate && !mergedChecked && 'ant-checkbox-indeterminate'
    );

    return (
      <StyledCheckboxWrapper
        {...rest}
        ref={labelRef}
        className={wrapperClassName}
        style={style}
        onClick={(e) => e.stopPropagation()}
      >
        <span className={boxClassName}>
          <input
            ref={inputRef}
            type="checkbox"
            id={id}
            name={mergedName}
            checked={mergedChecked}
            disabled={mergedDisabled}
            tabIndex={tabIndex}
            onClick={onClick}
            onChange={handleChange}
            value={value}
          />
        </span>
        {children !== undefined && children !== null && (
          <span className="ant-checkbox-label">{children}</span>
        )}
      </StyledCheckboxWrapper>
    );
  }
);

InternalCheckbox.displayName = 'Checkbox';

// --- CheckboxGroup Component ---

export const CheckboxGroup = React.forwardRef<HTMLDivElement, CheckboxGroupProps>(
  (
    {
      options,
      value: valueProp,
      defaultValue,
      onChange,
      disabled,
      name,
      children,
      className,
      style,
    },
    ref
  ) => {
    const [value, setValue] = useState<any[]>(valueProp ?? defaultValue ?? []);
    const [registeredValues, setRegisteredValues] = useState<any[]>([]);

    // Sync controlled value
    useEffect(() => {
      if (valueProp !== undefined) {
        setValue(valueProp);
      }
    }, [valueProp]);

    const memoizedOptions = useMemo(() => {
      if (!options) return [];
      return options.map((opt) => {
        if (typeof opt === 'string' || typeof opt === 'number') {
          return { label: String(opt), value: opt };
        }
        return opt;
      });
    }, [options]);

    const registerValue = useCallback((val: any) => {
      setRegisteredValues((prev) => (prev.includes(val) ? prev : [...prev, val]));
    }, []);

    const cancelValue = useCallback((val: any) => {
      setRegisteredValues((prev) => prev.filter((v) => v !== val));
    }, []);

    const toggleOption = useCallback(
      (option: { label: React.ReactNode; value: any }) => {
        const optionIndex = value.indexOf(option.value);
        const newValue = [...value];
        if (optionIndex === -1) {
          newValue.push(option.value);
        } else {
          newValue.splice(optionIndex, 1);
        }

        if (valueProp === undefined) {
          setValue(newValue);
        }

        // Filter to registered values and sort by option order
        const filteredSorted = newValue
          .filter((v) => registeredValues.includes(v))
          .sort((a, b) => {
            const indexA = memoizedOptions.findIndex((opt) => opt.value === a);
            const indexB = memoizedOptions.findIndex((opt) => opt.value === b);
            return indexA - indexB;
          });

        onChange?.(filteredSorted);
      },
      [value, valueProp, registeredValues, memoizedOptions, onChange]
    );

    const contextValue = useMemo<CheckboxGroupContextValue>(
      () => ({
        toggleOption,
        value,
        disabled,
        name,
        registerValue,
        cancelValue,
      }),
      [toggleOption, value, disabled, name, registerValue, cancelValue]
    );

    const groupClassName = cx(ANTISH, 'ant-checkbox-group', className);

    return (
      <GroupContext.Provider value={contextValue}>
        <StyledCheckboxGroup ref={ref} className={groupClassName} style={style} role="group">
          {options
            ? memoizedOptions.map((opt) => (
                <InternalCheckbox
                  key={String(opt.value)}
                  disabled={opt.disabled ?? disabled}
                  value={opt.value}
                  checked={value.includes(opt.value)}
                  onChange={opt.onChange}
                  className={opt.className}
                  style={opt.style}
                >
                  {opt.label}
                </InternalCheckbox>
              ))
            : children}
        </StyledCheckboxGroup>
      </GroupContext.Provider>
    );
  }
);

CheckboxGroup.displayName = 'CheckboxGroup';

// --- Compound Component Export ---

type CompoundedCheckbox = typeof InternalCheckbox & {
  Group: typeof CheckboxGroup;
  GroupWrapper: typeof GroupWrapper;
};

const Checkbox = InternalCheckbox as CompoundedCheckbox;
Checkbox.Group = CheckboxGroup;
Checkbox.GroupWrapper = GroupWrapper;

export default Checkbox;
